import { Button, Modal, Select, Space, Table } from "antd";
import { useModal } from "@refinedev/antd";
import { useCreate, useDelete, useList } from "@refinedev/core";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import { PlayerAwardItem, PlayerAwardLink } from "../../../../Awards/Lib";
import { AppImagePreview } from "../../../../../components";
import {
  DayMonthYear,
  errorNotification,
  successNotification,
} from "../../../../../helpers";
import s from "../../../../News/News-List/All-News/All-News.module.scss";

type PlayerAwardsProps = {
  playerSlug: string | null;
  fnlPlayerName: string;
};

const getAwardTypeLabel = (award: PlayerAwardItem | PlayerAwardLink) =>
  award.typeDisplayName ||
  award.typeName ||
  award.awardTypeDisplayName ||
  award.awardTypeName ||
  "—";

const getAwardImage = (award: PlayerAwardItem | PlayerAwardLink) =>
  award.typeImageUrl || award.awardImage || award.imageUrl || "";

// Для /cms/awards/player нужен именно rewardId награды, не awardTypeId.
const getRewardId = (award: PlayerAwardLink) =>
  award.rewardId ?? award.awardId ?? award.id;

export const PlayerAwards = ({
  playerSlug,
  fnlPlayerName,
}: PlayerAwardsProps) => {
  const { modalProps, show, close } = useModal();
  const modalState = modalProps as { open?: boolean; visible?: boolean };
  const isOpen = Boolean(modalState.open ?? modalState.visible);

  const [selectedAwardId, setSelectedAwardId] = useState<number | undefined>();

  const { data: allAwardsData, isFetching: isAllAwardsFetching } =
    useList<PlayerAwardItem>({
      dataProviderName: "awardsDataProvider",
      resource: "awards",
      queryOptions: {
        enabled: isOpen,
      },
    });

  const {
    data: playerAwardsData,
    isFetching: isPlayerAwardsFetching,
    refetch: refetchPlayerAwards,
  } = useList<PlayerAwardLink>({
    dataProviderName: "playerAwardsDataProvider",
    resource: "player-awards",
    filters: [
      {
        field: "playerSlug",
        operator: "eq",
        value: playerSlug ?? undefined,
      },
    ],
    queryOptions: {
      enabled: isOpen && Boolean(playerSlug),
    },
    ...errorNotification({}),
  });

  const allAwards = (allAwardsData?.data ?? []) as PlayerAwardItem[];
  const attachedAwards = (playerAwardsData?.data ?? []) as PlayerAwardLink[];

  const attachedIdSet = useMemo(() => {
    return new Set(attachedAwards.map((award) => String(getRewardId(award))));
  }, [attachedAwards]);

  const availableAwards = useMemo(() => {
    return allAwards.filter((award) => !attachedIdSet.has(String(award.id)));
  }, [allAwards, attachedIdSet]);

  const { mutate: attachAward, isLoading: isAttaching } = useCreate();
  const { mutate: detachAward, isLoading: isDetaching } = useDelete();

  const onAttach = () => {
    if (!selectedAwardId || !playerSlug) return;

    attachAward(
      {
        dataProviderName: "playerAwardsDataProvider",
        resource: "player-awards",
        values: { awardId: selectedAwardId },
        meta: { playerSlug },
        ...successNotification({
          successData: { message: "Награда прикреплена", type: "success" },
          callback: () => {
            setSelectedAwardId(undefined);
            refetchPlayerAwards();
          },
        }),
        ...errorNotification({}),
      },
      { onSuccess: () => null }
    );
  };

  const onDetach = (rewardId: string | number) => {
    detachAward(
      {
        dataProviderName: "playerAwardsDataProvider",
        resource: "player-awards",
        id: rewardId,
        meta: { playerSlug },
        ...successNotification({
          successData: { message: "Награда откреплена", type: "success" },
          callback: () => refetchPlayerAwards(),
        }),
        ...errorNotification({}),
      },
      { onSuccess: () => null }
    );
  };

  const loading = isAllAwardsFetching || isPlayerAwardsFetching;
  const disabledAttach = !playerSlug || loading;

  return (
    <>
      <Button size="small" onClick={show} block>
        Награды
      </Button>
      <Modal
        title={`Награды игрока: ${fnlPlayerName}`}
        onCancel={() => {
          close();
          setSelectedAwardId(undefined);
        }}
        footer={null}
        width={900}
        {...modalProps}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!playerSlug && (
            <div style={{ color: "var(--danger)" }}>
              У игрока не задан slug — сначала добавьте slug, затем можно
              прикреплять награды.
            </div>
          )}

          <Space
            direction="horizontal"
            size={12}
            style={{ width: "100%" }}
            wrap
          >
            <Select
              style={{ minWidth: 320, flex: 1 }}
              placeholder="Выберите награду для прикрепления"
              value={selectedAwardId}
              onChange={(value) => setSelectedAwardId(Number(value))}
              options={availableAwards.map((award) => ({
                value: Number(award.id),
                label: `${getAwardTypeLabel(award)}: ${award.text}`,
              }))}
              showSearch
              optionFilterProp="label"
              disabled={disabledAttach}
            />

            <Button
              type="primary"
              onClick={onAttach}
              disabled={!selectedAwardId || disabledAttach}
              loading={isAttaching}
            >
              Прикрепить
            </Button>
          </Space>

          <Table<PlayerAwardLink>
            dataSource={attachedAwards}
            pagination={false}
            loading={loading}
            rowKey={(record) => String(getRewardId(record))}
            locale={{
              emptyText: (
                <div style={{ margin: 24 }}>Награды не прикреплены</div>
              ),
            }}
            showSorterTooltip={false}
          >
            <Table.Column<PlayerAwardLink>
              width={140}
              key="imageUrl"
              title={"Изображение"}
              render={(_, record) => (
                <AppImagePreview
                  imageURL={getAwardImage(record)}
                  useS3={
                    Boolean(getAwardImage(record)) &&
                    !String(getAwardImage(record)).startsWith("http")
                  }
                  sharedStyles={s.imageUrl}
                />
              )}
            />
            <Table.Column<PlayerAwardLink>
              width={260}
              key="awardType"
              title="Номинация"
              render={(_, record) => getAwardTypeLabel(record)}
            />
            <Table.Column<PlayerAwardLink>
              dataIndex={"text"}
              key="text"
              title="Описание"
            />
            <Table.Column<PlayerAwardLink>
              dataIndex={"dateFrom"}
              key="dateFrom"
              width={160}
              title="Дата"
              render={(value) =>
                value ? dayjs(value).format(DayMonthYear) : "—"
              }
            />

            <Table.Column<PlayerAwardLink>
              width={140}
              key="actions"
              title="Действия"
              render={(_, record) => (
                <Button
                  danger
                  size="small"
                  onClick={() => onDetach(getRewardId(record) || "")}
                  loading={isDetaching}
                >
                  Открепить
                </Button>
              )}
            />
          </Table>
        </Space>
      </Modal>
    </>
  );
};
